import { format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { useCodeSessionList } from "@/api/code-sessions/useCodeSessionList";
import { resolveDateFnsLocale } from "@/components/notifications/utils/resolve-date-fns-locale";
import { SidebarMenuItem } from "@/components/ui/sidebar";
import { PATHS } from "@/constants/paths";
import { cn } from "@/lib/utils";
import { SESSION_DOT_CLASS_MAP } from "@/pages/code/constants/status-dot";
import type { ISessionDayGroup } from "@/pages/code/utils/group-sessions-by-day";
import { groupSessionsByDay } from "@/pages/code/utils/group-sessions-by-day";

// NOTE: The handoff swaps the chat history block for this one while the Code
// section is open, so the sidebar always lists what the current section is
// about. Sessions come from the same list query the Code home page uses, so
// opening a session does not cost a second request.
export const CodeSessionSidebarList = React.memo(() => {
  const { t, i18n } = useTranslation("code");
  const { data: sessions } = useCodeSessionList();
  const groups = groupSessionsByDay(sessions ?? []);

  const resolveGroupLabel = (group: ISessionDayGroup): string => {
    if (group.kind === "today") {
      return t("sidebar.today");
    }
    if (group.kind === "yesterday") {
      return t("sidebar.yesterday");
    }
    return format(new Date(group.date), "d MMM", {
      locale: resolveDateFnsLocale(i18n.language),
    });
  };

  return (
    <>
      <SidebarMenuItem className="pb-3 pt-3 text-sm font-medium text-foreground-secondary">
        {t("sidebar.sessionsTitle")}
      </SidebarMenuItem>

      {groups.length === 0 && (
        <SidebarMenuItem
          className="pb-2 text-sm text-foreground-secondary"
          data-testid="sidebar-code-sessions-empty"
        >
          {t("sidebar.empty")}
        </SidebarMenuItem>
      )}

      {groups.map((group) => (
        <SidebarMenuItem key={group.id} className="flex flex-col">
          <span
            data-testid="sidebar-code-session-day"
            className="pb-1 pt-2 text-xs font-medium text-foreground-secondary"
          >
            {resolveGroupLabel(group)}
          </span>
          {group.sessions.map((session) => (
            <NavLink
              key={session.uuid}
              to={PATHS.CODE_DETAIL.replace(":sessionId", session.uuid)}
              data-testid="sidebar-code-session"
              className={({ isActive }) =>
                cn(
                  "flex h-10 items-center gap-2 rounded-md px-2 text-sm text-foreground transition-colors hover:bg-sidebar-accent",
                  isActive && "bg-accent text-primary-dark",
                )
              }
            >
              <span
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  SESSION_DOT_CLASS_MAP[session.status],
                )}
              />
              <span className="min-w-0 flex-1 truncate">{session.prompt}</span>
            </NavLink>
          ))}
        </SidebarMenuItem>
      ))}
    </>
  );
});

CodeSessionSidebarList.displayName = "CodeSessionSidebarList";
